import React from "react";
import { twMerge } from "tailwind-merge";
import { type ComponentFormatter, formatComponentValue } from "../../utils/formatting";
import { Badge } from "../Badge";
import { Icon } from "../Icon";
import { Skeleton } from "../Skeleton";

// Use the centralized formatter type
export type KpiFormatter = ComponentFormatter;

/**
 * Kpi Component — Single metric display
 *
 * Displays a key performance indicator with value, delta, trend, and status.
 * Designed to work standalone or within a KpiGroup for dashboard layouts.
 *
 * Usage:
 * ```tsx
 * <Kpi
 *   label="Active Sites"
 *   value={2481}
 *   delta={3.2}
 *   deltaPeriodLabel="vs last 7d"
 *   trend={dataPoints}
 * />
 * ```
 */

// ——————————————————————————————————————————
// Types
// ——————————————————————————————————————————

export type KpiStatus = "neutral" | "success" | "warning" | "error" | "info";
export type KpiSize = "sm" | "md" | "lg";
export type KpiOrientation = "vertical" | "horizontal";

export interface TrendPoint {
  x: number | string | Date;
  y: number;
}

export interface KpiProps {
  /** Short label describing the metric */
  label: React.ReactNode;
  /** Primary value - number for auto-formatting or string for custom display */
  value: number | string;
  /** Formatter using the unified formatting system */
  formatter?: KpiFormatter;
  /** Change since prior period */
  delta?: number;
  /** Description of delta period (e.g., "vs last 7d") */
  deltaPeriodLabel?: string;
  /** How to interpret delta direction */
  deltaIntent?: "upIsGood" | "downIsGood";
  /** Mini trend data points */
  trend?: TrendPoint[];
  /** Status indicator */
  status?: {
    value: string;
    tone?: KpiStatus;
  };
  /** Help text or tooltip content */
  helpText?: React.ReactNode;
  /** Size variant */
  size?: KpiSize;
  /** Layout orientation */
  orientation?: KpiOrientation;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state */
  empty?: boolean;
  /** Error state */
  error?: string | React.ReactNode;
  /** Click handler for drill-down */
  onClick?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Custom value renderer */
  renderValue?: (value: KpiProps["value"]) => React.ReactNode;
  /** Custom delta renderer */
  renderDelta?: (delta?: number) => React.ReactNode;
  /** Custom trend renderer */
  renderTrend?: (points?: TrendPoint[]) => React.ReactNode;
}

// ——————————————————————————————————————————
// Helpers
// ——————————————————————————————————————————

// Use the centralized formatting utility
function formatValue(value: number | string, formatter?: KpiFormatter): React.ReactNode {
  return formatComponentValue({
    value,
    formatter,
    emptyClassName: "text-text-muted",
    emptyText: "—",
  });
}

function getDeltaColor(delta: number, deltaIntent: "upIsGood" | "downIsGood" = "upIsGood"): string {
  if (delta === 0) return "text-text-muted";

  const isPositive = delta > 0;

  if (deltaIntent === "upIsGood") {
    return isPositive ? "text-feedback-success" : "text-feedback-error";
  } else {
    return isPositive ? "text-feedback-error" : "text-feedback-success";
  }
}

// ——————————————————————————————————————————
// Sparkline Component
// ——————————————————————————————————————————

function Sparkline({ points, className, ariaLabel }: { points: TrendPoint[]; className?: string; ariaLabel?: string }) {
  const width = 64;
  const height = 20;
  const padding = 2;

  // Extract y values
  const yValues = points.map((p) => (typeof p.y === "number" ? p.y : 0));

  if (yValues.length === 0) return null;

  // Calculate bounds
  const min = Math.min(...yValues);
  const max = Math.max(...yValues);
  const range = max - min;

  // Normalize y coordinate
  const normalizeY = (y: number) => {
    if (range === 0) return height / 2;
    return height - padding - ((y - min) / range) * (height - 2 * padding);
  };

  // Calculate x step
  const xStep = (width - 2 * padding) / Math.max(points.length - 1, 1);

  // Build path
  const pathData = points
    .map((point, index) => {
      const x = padding + index * xStep;
      const y = normalizeY(typeof point.y === "number" ? point.y : 0);
      return `${index === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");

  return (
    <svg
      width={width}
      height={height}
      role="img"
      aria-label={ariaLabel}
      className={twMerge("overflow-visible", className)}
    >
      <path d={pathData} fill="none" strokeWidth="1.5" className="stroke-border-muted" />
    </svg>
  );
}

// ——————————————————————————————————————————
// Main Component
// ——————————————————————————————————————————

export function Kpi({
  label,
  value,
  formatter,
  delta,
  deltaPeriodLabel,
  deltaIntent = "upIsGood",
  trend,
  status,
  helpText,
  size = "md",
  orientation = "vertical",
  isLoading,
  empty,
  error,
  onClick,
  className,
  renderValue,
  renderDelta,
  renderTrend,
}: KpiProps) {
  // Size classes
  const sizeClasses = {
    sm: {
      label: "text-xs",
      value: "text-base",
      delta: "text-xs",
      gap: "gap-1",
    },
    md: {
      label: "text-sm",
      value: "text-xl",
      delta: "text-sm",
      gap: "gap-2",
    },
    lg: {
      label: "text-base",
      value: "text-2xl",
      delta: "text-base",
      gap: "gap-3",
    },
  };

  const currentSize = sizeClasses[size];

  // Format value
  const valueNode = isLoading ? (
    <Skeleton className="h-7 w-20" />
  ) : empty ? (
    <span className="text-text-muted">—</span>
  ) : error ? (
    <span className="text-feedback-error text-sm">{typeof error === "string" ? error : "Error"}</span>
  ) : renderValue ? (
    renderValue(value)
  ) : (
    formatValue(value, formatter)
  );

  // Format delta
  const deltaNode = renderDelta ? (
    renderDelta(delta)
  ) : delta !== undefined && !isLoading && !empty && !error ? (
    <div className={twMerge(currentSize.delta, getDeltaColor(delta, deltaIntent), "flex items-center gap-1")}>
      {delta > 0 && <Icon name="ArrowUp" size={12} />}
      {delta < 0 && <Icon name="ArrowDown" size={12} />}
      {delta === 0 && <Icon name="Minus" size={12} />}
      <span>
        {delta > 0 ? "+" : ""}
        {Math.abs(delta).toFixed(1)}%
      </span>
      {deltaPeriodLabel && <span className="text-text-muted">· {deltaPeriodLabel}</span>}
    </div>
  ) : isLoading && delta !== undefined ? (
    <Skeleton className="h-4 w-24" />
  ) : null;

  // Trend node
  const trendNode = renderTrend ? (
    renderTrend(trend)
  ) : trend && trend.length > 0 && !isLoading && !error ? (
    <Sparkline points={trend} ariaLabel={`${label} trend`} className={empty ? "opacity-30" : ""} />
  ) : isLoading && trend !== undefined ? (
    <Skeleton className="h-5 w-16" />
  ) : null;

  return (
    <section
      className={twMerge(
        "flex min-w-0",
        orientation === "horizontal" ? `items-center ${currentSize.gap}` : `flex-col ${currentSize.gap}`,
        onClick && "cursor-pointer hover:bg-background-muted rounded-lg px-3 py-2 -mx-3 -my-2 transition-colors",
        className
      )}
      aria-labelledby={`kpi-${label}`}
      onClick={onClick}
    >
      {/* Label row */}
      <div className="flex items-center gap-2">
        <h3 id={`kpi-${label}`} className={twMerge(currentSize.label, "font-medium text-text-caption truncate")}>
          {isLoading ? <Skeleton className="h-4 w-24" /> : label}
        </h3>
        {helpText && !isLoading && (
          <Icon name="Question" size={12} className="text-text-muted cursor-help" aria-label="Help" />
        )}
      </div>

      {/* Value and status */}
      <div className="flex items-baseline gap-2">
        <output
          className={twMerge(currentSize.value, "font-semibold", error ? "text-feedback-error" : "text-text-heading")}
          aria-label={`Value: ${valueNode}`}
        >
          {valueNode}
        </output>
        {status && !isLoading && !error && (
          <Badge variant={status.tone || "neutral"} size="sm">
            {status.value}
          </Badge>
        )}
      </div>

      {/* Delta and trend row */}
      {(deltaNode || trendNode) && (
        <div className="flex items-center gap-3">
          {deltaNode}
          {trendNode}
        </div>
      )}
    </section>
  );
}
